/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { createObservableDataAction, IAction, IActionContext, IActionInput, ICreateActionContext } from '@msdyn365-commerce/core';
import { AsyncResult, ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 *  GetSearchByCriteria Input Action
 */

export class GetSearchByCriteriaInput implements IActionInput {
    public productIds?: number[];
    public pageType?: string;
    public context?: IActionContext;
    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    constructor(productIds?: number[], context?: IActionContext, pageType?: string) {
        // super({ shouldCacheOutput: true, cacheObjectType: 'postCodeRestriction', cacheKey: 'postCodeRestriction' });
        this.productIds = productIds;
        this.context = context;
        this.pageType = pageType;
    }

    public getCacheKey = () => `postCodeRestriction`;
    public getCacheObjectType = () => 'postCodeRestriction';
    public dataCacheType = () => {
        if (this.pageType !== 'checkout') {
            return 'request';
        } else {
            return 'application';
        }
    };
}

// Response of the action
export interface IGetSearchByCriteria {
    productsSearchResult: AsyncResult<ProductSearchResult[]>;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: ICreateActionContext): IActionInput => {
    return new GetSearchByCriteriaInput();
};
/**
 * Action function to fetch products complete details along with attributes
 */
export async function getSearchByCriteriaAsync(input: GetSearchByCriteriaInput, ctx: IActionContext): Promise<ProductSearchResult[]> {
    let productSearchResults: ProductSearchResult[];
    let promise: AsyncResult<ProductSearchResult[]>;
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = {
        ChannelId: ctx.requestContext.apiSettings.channelId,
        CatalogId: ctx.requestContext.apiSettings.catalogId
    };

    searchCriteriaInput.Ids = input.productIds;
    searchCriteriaInput.IncludeAttributes = true;

    promise = searchByCriteriaAsync(
        {
            callerContext: ctx,
            queryResultSettings: {}
        },
        searchCriteriaInput
    );

    productSearchResults = await promise;
    return productSearchResults;
}

export default createObservableDataAction({
    action: <IAction<ProductSearchResult[]>>getSearchByCriteriaAsync,
    input: createInput
});
